import React, { useState } from "react";
import { ArrowUpRight, Plus, Minus } from "lucide-react";

export const BOOKING_URL = "https://alchemy-practice.clientsecure.me/";

export const Eyebrow = ({ children, className = "" }) => (
    <span className={`editorial-eyebrow ${className}`}>{children}</span>
);

export const SageRule = ({ className = "" }) => (
    <div className={`mx-auto ${className}`} style={{ height: 2, width: 64, background: "#8A9E85" }} />
);

export const CTA = ({ children = "Schedule a Consultation", testid }) => (
    <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="editorial-button-primary group"
    >
        {children}
        <ArrowUpRight size={16} strokeWidth={1.25} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
);

export const ModalityHero = ({ eyebrow, headline, italic, subhead, testidPrefix }) => (
    <section data-testid={`${testidPrefix}-hero`} className="relative pt-44 pb-28 lg:pt-52 lg:pb-36 overflow-hidden" style={{ background: "#FAFAF8" }}>
        <div className="absolute top-32 left-12 hidden lg:block" aria-hidden>
            <div style={{ height: 1, width: 80, background: "#9B7D5A" }} />
            <div className="mt-3 text-gold text-sm">✦</div>
        </div>
        <div className="absolute bottom-24 right-12 hidden lg:block" aria-hidden>
            <div className="text-gold text-sm mb-3 text-right">✦</div>
            <div style={{ height: 1, width: 80, background: "#9B7D5A" }} />
        </div>
        <div className="relative mx-auto max-w-[1000px] px-6 lg:px-12 text-center" data-reveal>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[72px] leading-[1.04] text-charcoal text-balance">
                {headline} <em className="italic text-gold">{italic}</em>
            </h1>
            <SageRule className="mt-8" />
            <p className="mt-10 max-w-2xl mx-auto text-lg sm:text-xl leading-[1.85] text-charcoal-soft text-pretty">{subhead}</p>
            <div className="mt-12"><CTA testid={`${testidPrefix}-hero-cta`} /></div>
        </div>
    </section>
);

export const ProblemSection = ({ eyebrow, headline, italic, firstLine, paragraphs, testidPrefix }) => (
    <section data-testid={`${testidPrefix}-problem`} className="py-28 lg:py-36" style={{ background: "#FAFAF8" }}>
        <div className="mx-auto max-w-[800px] px-6 lg:px-12 text-center" data-reveal>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-charcoal text-balance">
                {headline} <em className="italic text-gold">{italic}</em>
            </h2>
            <SageRule className="mt-8" />
            <div className="mt-14 text-left">
                <p className="text-2xl sm:text-3xl font-serif italic text-charcoal drop-cap">{firstLine}</p>
                {paragraphs.map((p, i) => (
                    <p key={i} className="mt-10 text-lg leading-[1.95] text-charcoal-soft">{p}</p>
                ))}
            </div>
        </div>
    </section>
);

export const HowItHelpsSection = ({ eyebrow, headline, italic, paragraphs, quote, testidPrefix, extraContent }) => (
    <section data-testid={`${testidPrefix}-howithelps`} className="py-28 lg:py-36" style={{ background: "#EEF2ED" }}>
        <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-7" data-reveal>
                    <Eyebrow>{eyebrow}</Eyebrow>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal">
                        {headline} <em className="italic text-gold">{italic}</em>
                    </h2>
                    <div className="my-6" style={{ height: 2, width: 64, background: "#8A9E85" }} />
                    {paragraphs.map((p, i) => (
                        <p key={i} className={`${i === 0 ? "mt-6" : "mt-6"} text-lg leading-[1.95] text-charcoal-soft`}>{p}</p>
                    ))}
                    {extraContent}
                </div>
                <aside className="lg:col-span-5 lg:pt-16" data-reveal>
                    <blockquote style={{ borderLeft: "3px solid #9B7D5A", paddingLeft: "24px" }} className="font-serif italic text-charcoal">
                        <p className="leading-[1.45]" style={{ fontSize: "24px" }}>{quote}</p>
                        <footer className="mt-6 text-[11px] tracking-[0.32em] uppercase not-italic" style={{ color: "#9B7D5A", fontFamily: "Figtree" }}>
                            <span className="mr-3">✦</span> Austine, LPC
                        </footer>
                    </blockquote>
                </aside>
            </div>
        </div>
    </section>
);

export const ThePracticeSection = ({ eyebrow, headline, italic, paragraphs, testidPrefix }) => (
    <section data-testid={`${testidPrefix}-practice`} className="py-28 lg:py-36" style={{ background: "#F2EDE6" }}>
        <div className="mx-auto max-w-[820px] px-6 lg:px-12 text-center" data-reveal>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal text-balance">
                {headline} <em className="italic text-gold">{italic}</em>
            </h2>
            <SageRule className="mt-8" />
            <div className="mt-12 text-left">
                {paragraphs.map((p, i) => (
                    <p key={i} className={`${i === 0 ? "" : "mt-6"} text-lg leading-[1.95] text-charcoal-soft`}>{p}</p>
                ))}
            </div>
        </div>
    </section>
);

export const BeginTogetherSection = ({ steps, italic = "care.", title = "A process that unfolds with", testidPrefix }) => (
    <section data-testid={`${testidPrefix}-begin`} className="py-28 lg:py-36" style={{ background: "#EEF2ED" }}>
        <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
            <div className="text-center max-w-2xl mx-auto" data-reveal>
                <Eyebrow>How We'll Begin Together</Eyebrow>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal">
                    {title} <em className="italic text-gold">{italic}</em>
                </h2>
                <SageRule className="mt-8" />
            </div>
            <div className="mt-20 grid md:grid-cols-3 gap-px" style={{ background: "#E2DDD8" }} data-testid={`${testidPrefix}-begin-grid`}>
                {steps.map((s) => (
                    <article key={s.title} data-testid={`${testidPrefix}-step-${s.n.toLowerCase()}`} className="p-10 lg:p-12 group" style={{ background: "#FAFAF8" }} data-reveal>
                        <div className="flex items-baseline justify-between mb-8">
                            <span className="font-serif text-3xl text-gold">{s.n}</span>
                            <span className="font-serif text-gold text-lg leading-none transition-transform duration-700 group-hover:rotate-90">✦</span>
                        </div>
                        <h3 className="font-serif text-2xl sm:text-3xl text-charcoal leading-snug">{s.title}</h3>
                        <div className="h-px w-10 my-6" style={{ background: "#8A9E85" }} />
                        <p className="text-charcoal-soft leading-[1.85] text-base">{s.body}</p>
                    </article>
                ))}
            </div>
            <div className="text-center mt-16" data-reveal>
                <CTA testid={`${testidPrefix}-begin-cta`} />
                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[11px] tracking-[0.28em] uppercase" style={{ color: "#8A7968" }}>
                    <span className="flex items-center gap-3"><span className="text-gold text-base leading-none">✦</span> Secure Telehealth</span>
                    <span className="flex items-center gap-3"><span className="text-gold text-base leading-none">✦</span> Wisconsin & Colorado</span>
                </div>
            </div>
        </div>
    </section>
);

export const IssuesListSection = ({ eyebrow, headline, italic, items, testidPrefix }) => (
    <section data-testid={`${testidPrefix}-issues`} className="py-28 lg:py-36" style={{ background: "#FAFAF8" }}>
        <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-5" data-reveal>
                    <Eyebrow>{eyebrow}</Eyebrow>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl leading-[1.05] text-charcoal">
                        {headline} <em className="italic text-gold">{italic}</em>
                    </h2>
                    <div className="mt-6" style={{ height: 2, width: 64, background: "#8A9E85" }} />
                </div>
                <div className="lg:col-span-7 lg:pt-2" data-reveal>
                    <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-5" data-testid={`${testidPrefix}-issues-list`}>
                        {items.map((it) => (
                            <li key={it} className="flex items-start gap-4 text-charcoal-soft leading-[1.7] pb-5" style={{ borderBottom: "1px solid #E2DDD8" }}>
                                <span className="text-gold leading-none mt-1.5 flex-shrink-0">✦</span>
                                <span>{it}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    </section>
);

const FAQItem = ({ q, a, idx, testidPrefix }) => {
    const [open, setOpen] = useState(idx === 0);
    return (
        <div className="border-b" style={{ borderColor: "#E2DDD8" }} data-testid={`${testidPrefix}-faq-${idx}`}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-start justify-between gap-6 py-7 text-left group"
                data-testid={`${testidPrefix}-faq-trigger-${idx}`}
            >
                <span className="font-serif text-2xl sm:text-3xl text-charcoal pr-4 group-hover:text-gold transition-colors duration-500">{q}</span>
                <span className="flex-shrink-0 mt-2 text-gold">
                    {open ? <Minus strokeWidth={1.25} size={22} /> : <Plus strokeWidth={1.25} size={22} />}
                </span>
            </button>
            <div className={`grid transition-all duration-700 ease-out ${open ? "grid-rows-[1fr] opacity-100 pb-8" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                    <p className="max-w-3xl text-charcoal-soft text-lg leading-[1.85] pr-12">{a}</p>
                </div>
            </div>
        </div>
    );
};

export const FAQSection = ({ heading, italic, faqs, testidPrefix, bg = "#EEF2ED" }) => (
    <section data-testid={`${testidPrefix}-faq-section`} className="py-28 lg:py-36" style={{ background: bg }}>
        <div className="mx-auto max-w-[1000px] px-6 lg:px-12">
            <div className="text-center mb-12" data-reveal>
                <Eyebrow>Considerations</Eyebrow>
                <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal">
                    {heading} <em className="italic text-gold">{italic}</em>
                </h2>
                <SageRule className="mt-8" />
            </div>
            <div data-reveal>
                {faqs.map((f, i) => <FAQItem key={i} idx={i} q={f.q} a={f.a} testidPrefix={testidPrefix} />)}
            </div>
        </div>
    </section>
);
